import React, { useEffect, useRef, useState } from 'react'
import { string, array, object, number, bool } from 'prop-types'
import styles from './EventContentGrid.module.scss'
import Grid from '@/component/Primitive/Grid'
import Card from './components/Card'
import VideoCard from './components/VideoCard'
import SponsorCard from './components/SponsorCard'
import SpeakerCard from './components/SpeakerCard'
import AgendaItemCard from './components/AgendaItemCard'
import EventCard from './components/EventCard'
import EventUmbrellaCard from './components/EventUmbrellaCard'
import cx from 'classnames'
import moment from 'moment'
import Container from '@/component/Primitive/Container'
import Stack from '@/component/Primitive/Stack'
import { Highlight, ButtonGroup } from '@/component/Primitive/Events'
import Type from '@/component/Primitive/Type'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import Icon from '@/component/Primitive/Icon'

export const EventContentGrid = ({
  list,
  theme,
  title,
  subtitle,
  buttonGroup,
  baseUrl,
  limit,
  openInNewTab,
  isCarousel
}) => {
  const dayMap = {}
  const inverseDayMap = {}
  const splideWrapperRef = useRef(null)
  const align = list[0] ? list[0]?.align : 'left'
  const contentType = list[0] ? list[0]?.type : 'video'
  const [buttonTop, setButtonTop] = useState('50%')
  let hideStageLabel = false

  useEffect(() => {
    if (!isCarousel) {
      return
    }

    const controller = new AbortController()
    const handleResize = () => {
      const imgs = splideWrapperRef?.current?.getElementsByTagName('img')
      if (imgs && imgs.length) {
        const img = [...imgs].filter((img) => img.checkVisibility())?.[0]

        if (img) {
          const top = img.scrollHeight / 2
          setButtonTop(`${top}px`)
        }
      }
    }

    window.addEventListener('load', handleResize, controller)
    window.addEventListener('resize', handleResize, controller)

    return () => {
      controller.abort()
    }
  }, [isCarousel])

  if (contentType === 'agendaItem') {
    let dayCount = 1
    let lastAgendaDate = null
    for (const item of list) {
      if (!lastAgendaDate) {
        dayMap[dayCount] = [{ ...item }]
        inverseDayMap[item._id] = dayCount
        lastAgendaDate = item.startDate
      } else {
        const isSameDay = moment(item.startDate).isSame(lastAgendaDate, 'day')
        if (isSameDay) {
          dayMap[dayCount].push({ ...item })
          inverseDayMap[item._id] = dayCount
          lastAgendaDate = item.startDate
        } else {
          dayCount++
          dayMap[dayCount] = [{ ...item }]
          inverseDayMap[item._id] = dayCount
          lastAgendaDate = item.startDate
        }
      }
    }
    hideStageLabel = Object.keys(dayMap).length === 1
  }

  const mapContentPropsToProps = (content, i) => {
    switch (content.type) {
      case 'video':
        return {
          ...content,
          tag: 'On Demand Video',
          key: content.video.name,
          title:
            content.title || content.video.name.length < 60
              ? content.video.name
              : content.video.name.slice(0, 60) + '...',
          subtitle: content.subtitle || content.video.eventName,
          description: content.video.description || 'description...',
          ctaLabel: content.ctaLabel || 'Watch now',
          theme: content.theme || theme,
          align: align || 'left',
          numItemsInRow: { m: 1, t: 2, d: 4 },
          sessionType: content.video.agendaItemSessionType,
          previewId: content.video.previewId,
          openInNewTab
        }
      case 'article':
        return {
          ...content,
          tag: 'Event Article',
          key: content.article.headline,
          title: content.title || content.article.headline,
          subtitle: content.subtitle || content.article.category || 'subtitle',
          description:
            content.description || content.article.sell || '...description',
          ctaLabel: content.ctaLabel || 'Read full article',
          theme: content.theme || theme,
          openInNewTab
        }
      case 'sponsor':
        return {
          ...content,
          tag: 'Event Sponsor',
          key: content.sponsor.displayName,
          title: content.title || content.sponsor.displayName,
          subtitle: content.sponsor.tier.name
            ? content.sponsor.tier.name.charAt(0).toUpperCase() +
              content.sponsor.tier.name.slice(1)
            : 'Partner',
          description: content.description,
          ctaLabel: content.ctaLabel || 'Visit sponsor booth',
          logo: content.logo,
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 2, t: 4, d: 6 },
          openInNewTab
        }
      case 'speaker':
        return {
          ...content,
          tag: 'Event Speaker',
          key: content.speaker.name,
          title: content.title || content.speaker.name,
          subtitle: content.subtitle || content.speaker.jobTitle,
          companyName: content.speaker.companyName,
          roles: content.speaker.roles,
          nextAgendaItem: content.speaker.nextAgendaItem,
          nextAgendaItems: content.speaker.nextAgendaItems,
          description: content.description,
          ctaLabel: content.ctaLabel || 'Read speaker bio',
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 2, t: 3, d: 4 },
          openInNewTab
        }
      case 'agendaItem': {
        const previousStartDate = list[i - 1]?.startDate
        const currentStartDate = content.startDate

        const showDatePrefix =
          !moment(previousStartDate).isSame(currentStartDate, 'day') ||
          !previousStartDate

        return {
          ...content,
          tag: 'Event Agenda',
          key: content.agendaItem.name,
          title: content.title || content.agendaItem.name,
          subtitle: content.subtitle || content.agendaItem.sessionType.name,
          description:
            content.description || content.agendaItem.description || '',
          speakers: content.agendaItem.speakers,
          sessionType: content.agendaItem.sessionType.shortName,
          time: `${content.localeSafeStartTime} - ${content.localeSafeEndTime} (${content.timezoneAbbreviation})`,
          startDate: content.agendaItem.startDate,
          endDate: content.agendaItem.endDate,
          ctaLabel: content.ctaLabel || 'Read about topic',
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 1, t: 1, d: 1 },
          shaded: i % 2 === 0,
          showDatePrefix,
          day: Object.keys(dayMap).length > 1 && inverseDayMap[content._id],
          baseUrl,
          location: content.agendaItem.location,
          hideStageLabel: limit ? false : hideStageLabel,
          openInNewTab
        }
      }
      case 'event': {
        const startDate = moment(content.event.startDate)
        const endDate = moment(content.event.endDate)
        let dateRange
        if (startDate.isSame(content.event.endDate, 'day')) {
          dateRange = `${startDate.format('MMM DD YYYY')}`
        } else {
          dateRange = `${startDate.format('MMM DD')}-${endDate.format(
            'Do YYYY'
          )}`
        }

        return {
          ...content,
          tag: 'Event',
          key: content.event.name,
          title: content.title || content.event.name,
          subtitle:
            content.subtitle || dateRange + ', ' + content.event.buildingName,
          description: content.description || content.event.description,
          ctaLabel: content.ctaLabel,
          theme: content.theme || theme,
          numItemsInRow: { m: 2, t: 3, d: 4 },
          align: align || 'left',
          openInNewTab
        }
      }
      case 'eventUmbrella':
        return {
          ...content,
          tag: 'Event Umbrella',
          key: content.eventUmbrella.name,
          title: content.title || content.eventUmbrella.name,
          subtitle: content.subtitle,
          description: content.description || content.eventUmbrella.description,
          ctaLabel: content.ctaLabel,
          theme: content.theme || theme,
          align: 'center',
          openInNewTab
        }
      default:
        break
    }
    return null
  }

  const ComponentMap = {
    video: VideoCard,
    article: Card,
    sponsor: SponsorCard,
    speaker: SpeakerCard,
    agendaItem: AgendaItemCard,
    event: EventCard,
    eventUmbrella: EventUmbrellaCard
  }

  const Wrapper = contentType === 'agendaItem' ? 'div' : Grid
  const Inner = contentType === 'agendaItem' ? 'div' : Grid.Item

  return (
    <div
      className={cx(
        styles.EventContentGrid,
        theme === 'grey' && styles.grey,
        theme === 'dark' && styles.dark,
        theme === 'darkGrey' && styles.darkGrey,
        align === 'center' && styles.center,
        contentType === 'video' && styles.video
      )}
    >
      <Container
        center
        size={contentType === 'speaker' ? 'large' : 'wide'}
        gutter
        className={styles.EventDetailsContainer}
      >
        <Stack gap="medium" className={styles.Stack}>
          <div gap="small" className={styles.StackInner}>
            <div className={styles.TitleWrapper}>
              {title && (
                <Type
                  as="h2"
                  className={styles.Title}
                  size={['heading5', 'heading2']}
                  weight="bold"
                >
                  <Highlight
                    classNames={{
                      strong: styles.Highlight
                    }}
                  >
                    {title}
                  </Highlight>
                </Type>
              )}
              {subtitle && (
                <Type as="p" className={styles.Subtitle} size="body4">
                  {subtitle}
                </Type>
              )}
            </div>
            {!!buttonGroup?.length && contentType === 'video' && (
              <ButtonGroup
                group={buttonGroup}
                className={cx(
                  styles.ButtonGroup,
                  contentType === 'video' && styles.videoDesktopOnly
                )}
                center={align === 'center'}
                theme={theme}
              />
            )}
          </div>
          {isCarousel && list.length && contentType !== 'agendaItem' ? (
            <div
              ref={splideWrapperRef}
              style={{ '--button-top': buttonTop }}
              className={styles.Carousel}
            >
              <Splide
                hasTrack={false}
                options={{
                  gap: '20px',
                  perPage:
                    mapContentPropsToProps(list?.[0], 0)?.numItemsInRow?.d || 4,
                  breakpoints: {
                    640: {
                      perPage: 1
                    },
                    768: {
                      perPage:
                        mapContentPropsToProps(list?.[0], 0)?.numItemsInRow
                          ?.m || 2
                    },
                    960: {
                      perPage:
                        mapContentPropsToProps(list?.[0], 0)?.numItemsInRow
                          ?.t || 3
                    }
                  }
                }}
              >
                <SplideTrack>
                  {list?.map((content, i) => {
                    const Component = ComponentMap[content.type]
                    const embellishedProps = mapContentPropsToProps(content, i)
                    return (
                      <SplideSlide key={`${embellishedProps.key}-${i}`}>
                        <Component theme={theme} {...embellishedProps} />
                      </SplideSlide>
                    )
                  })}
                </SplideTrack>

                <div className={cx('splide__arrows', styles.CarouselButtons)}>
                  <button
                    className={cx(
                      'splide__arrow',
                      'splide__arrow--prev',
                      styles.CarouselButton
                    )}
                  >
                    <Icon width="7" type="arrow-left" />
                  </button>
                  <button
                    className={cx(
                      'splide__arrow',
                      'splide__arrow--next',
                      styles.CarouselButton
                    )}
                  >
                    <Icon width="7" type="arrow-right" />
                  </button>
                </div>
              </Splide>
            </div>
          ) : (
            <Wrapper size="small" flex gutter="default" className={styles.Grid}>
              {list?.map((content, i) => {
                const Component = ComponentMap[content.type]
                const embellishedProps = mapContentPropsToProps(content, i)
                return (
                  <Inner
                    key={`${embellishedProps.key}-${i}`}
                    width={{
                      m: 1 / (embellishedProps?.numItemsInRow?.m || 2),
                      t: 1 / (embellishedProps?.numItemsInRow?.t || 4),
                      d: 1 / (embellishedProps?.numItemsInRow?.d || 4)
                    }}
                    trailingGap="default"
                  >
                    <Component theme={theme} {...embellishedProps} />
                  </Inner>
                )
              })}
            </Wrapper>
          )}
          {!!buttonGroup?.length && (
            <ButtonGroup
              group={buttonGroup}
              className={cx(
                styles.ButtonGroup,
                contentType === 'video' && styles.videoMobileOnly
              )}
              center={align === 'center'}
              theme={theme}
            />
          )}
        </Stack>
      </Container>
    </div>
  )
}

EventContentGrid.propTypes = {
  openInNewTab: bool,
  list: array,
  theme: string,
  image: object,
  title: string,
  subtitle: string,
  buttonGroup: array,
  baseUrl: string,
  limit: number,
  isCarousel: bool
}
